import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Separator,
  Flex,
  IconButton,
  Input,
  InputGroup,
  Text,
  useDisclosure,
  Field,
  Icon,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, useEffect, useState } from "react";
import { Access, AccessUpdateRequest } from "../../../access/models/Access";
import { AccessItem } from "./AccessItem";
import { t } from "i18next";
import { isValidEmail } from "../../utils/validation-utils";
import { UserNotFoundPrompt } from "../UserNotFoundPrompt/UserNotFoundPrompt";
import { toast } from "../CustomToaster";
import { AddIcon, CopyIcon, LockIcon, UnlockIcon } from "../icons";
import { CustomDialog } from "../CustomDialog";
import { UserApi } from "../../../account/services/UserApi";

interface Props {
  name: string;
  publicRoute: string;
  isShared: boolean;
  password?: string;
  accesses: Access[];
  topLevelAccesses?: Access[];
  onClose: () => any;
  toggleSharing: () => any;
  addAccess: (email: string) => Promise<Access>;
  updateAccess: (accessId: string, data: AccessUpdateRequest) => any;
  removeAccess: (accessId: string) => any;
}

const SharingModalBase: React.FC<Props> = ({
  name,
  publicRoute,
  isShared,
  password,
  accesses,
  topLevelAccesses,
  onClose,
  toggleSharing,
  addAccess,
  updateAccess,
  removeAccess,
}) => {
  const [email, setEmail] = useState("");
  const createUserModal = useDisclosure();
  const url = `${process.env.REACT_APP_BASE_URL}${publicRoute}`;

  useEffect(() => {
    if (email) {
      setEmail("");
    }
    // eslint-disable-next-line
  }, [accesses]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast("success", t("shared:shareModal.copied"));
  };

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const addUser = async () => {
    const userExists = await UserApi.exists(email).then((r) => r.data);

    if (userExists) {
      addAccess(email.trim())
        .then((newAccess) => {
          toast(
            "success",
            t("shared:shareModal.toast.accessGiven", {
              user: newAccess.email,
              resource: name,
            })
          );
        })
        .catch((error) => {
          const errorCode = error?.response?.data?.messageCode;
          if (errorCode === "updating_own_access") {
            toast("error", t("shared:shareModal.toast.updatingOwnAccess"));
          } else if (errorCode === "constraint_violation") {
            toast("error", t("shared:shareModal.toast.userAlreadyInList"));
          } else {
            toast("error", t("shared:shareModal.toast.invalidEmail"));
          }
        });
    } else {
      createUserModal.onOpen();
    }
  };

  const emailIsValid = isValidEmail(email);

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={`${t("shared:shareModal.title")} "${name}"`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (emailIsValid) addUser();
        }}
      >
        <Flex alignItems="center">
          <Field.Root invalid={!!email && !emailIsValid}>
            <InputGroup
              endElement={
                <div>
                  <IconButton
                    onClick={addUser}
                    variant="ghost"
                    disabled={!emailIsValid}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              }
            >
              <Input
                required
                value={email}
                onChange={handleChangeEmail}
                placeholder={t("shared:shareModal.addPeople")}
              />
            </InputGroup>
            <Field.ErrorText>
              {t("shared:shareModal.invalidEmail")}
            </Field.ErrorText>
          </Field.Root>
        </Flex>
      </form>

      <Text mt={8} mb={2} fontWeight="semibold">
        {t("shared:shareModal.peopleWithAccess")}
      </Text>

      <Box>
        {accesses.map((access, i) => (
          <AccessItem
            key={i}
            access={access}
            updateAccess={updateAccess}
            removeAccess={removeAccess}
          />
        ))}
      </Box>

      {!!topLevelAccesses && (
        <Card.Root variant={"subtle"} mt={2} p={4}>
          <Text fontWeight="semibold" mb={2}>
            {t("shared:shareModal.inheritedAccess")}
          </Text>
          <Box>
            {topLevelAccesses.map((access, i) => (
              <AccessItem
                key={i}
                isInherited
                access={access}
                updateAccess={updateAccess}
                removeAccess={removeAccess}
              />
            ))}
          </Box>
        </Card.Root>
      )}

      <Separator mt={6} mb={8} />

      <Text mb={4} fontWeight="semibold">
        {t("shared:shareModal.linkTitle")}
      </Text>

      {isShared && (
        <Flex pb={6}>
          <Field.Root>
            <Field.Label>
              {t("shared:shareModal.linkLabel")}
              <Icon ml={2} as={CopyIcon} />
            </Field.Label>
            <Input onClick={() => copyToClipboard(url)} value={url} readOnly />
          </Field.Root>
          <Box pl={4} />
          <Field.Root>
            <Field.Label>
              {t("shared:shareModal.passwordLabel")}
              <Icon ml={2} as={CopyIcon} />
            </Field.Label>
            <Input
              onClick={() => copyToClipboard(password!)}
              value={password}
              readOnly
            />
          </Field.Root>
        </Flex>
      )}

      <ButtonGroup pb={4} colorPalette={"primary"}>
        <Button onClick={toggleSharing}>
          {isShared ? <LockIcon /> : <UnlockIcon />}{" "}
          {isShared
            ? t("shared:shareModal.revokeButton")
            : t("shared:shareModal.shareButton")}
        </Button>
      </ButtonGroup>

      {createUserModal.open && (
        <UserNotFoundPrompt
          onClose={createUserModal.onClose}
          email={email}
          onConfirm={addUser}
        />
      )}
    </CustomDialog>
  );
};

export const SharingModal = observer(SharingModalBase);
